import type { Tuple } from '../types'

export function throwIfNever(value: never): never {
  throw new Error(`Получено значение ${value} для которого отсутствует обработчик`)
}

export const isString = (value: unknown): value is string => typeof value === 'string'
export const isNumber = (value: unknown): value is number => typeof value === 'number'
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean'
export const isNull = (value: unknown): value is null => value === null

export const isOfType =
  <TargetType>(boundary: readonly TargetType[]) =>
  (toTest: unknown): toTest is TargetType =>
    boundary.some(b => b === toTest)

export const isObject = (candidate: unknown): candidate is object =>
  typeof candidate === 'object' && candidate != null

export const isDefined = <T>(value: T | null | undefined): value is T =>
  !(value == null || value === '')

export const hasKeys = <Keys extends string[], Values>(
  object: Record<PropertyKey, Values>,
  ...keys: Keys
): object is Record<PropertyKey, Values> & Record<Keys[number], Values> =>
  keys.every(key => key in object)

export const hasKeysOfUnknown = <Keys extends string>(
  obj: object,
  ...keys: Keys[]
): obj is object & Record<Keys, unknown> => keys.every(key => key in obj)

export function hasKeysLoose(candidate: object, ...keys: string[]) {
  const containedKeys = Object.keys(candidate)
  return containedKeys.every(key => keys.includes(key))
}

export const hasKeysExact = <Keys extends string>(
  obj: object,
  ...keys: Keys[]
): obj is Record<Keys, unknown> => {
  const objectKeys = Object.keys(obj)
  return (
    objectKeys.every(objectKey => keys.some(key => objectKey === key)) &&
    keys.every(key => objectKeys.includes(key))
  )
}

export function assert<E extends Error>(
  condition: boolean,
  message: E | string = 'Condition was false',
): asserts condition {
  if (!condition) {
    const objectToThrow = typeof message === 'string' ? new Error(message) : message
    throw objectToThrow
  }
}

type MessageFormatter<T> = (value: T, index: number) => string
const DEFAULT_MESSAGE_FORMATTER: MessageFormatter<unknown> = (_, index) =>
  `Element at index ${index} does no satisfy the condition`

export function assertArray<A, B extends A>(
  array: A[],
  condition: (item: A) => item is B,
  message: string | MessageFormatter<A> = DEFAULT_MESSAGE_FORMATTER,
): asserts array is B[] {
  let index = 0
  for (const item of array) {
    if (!condition(item)) {
      const errorMessage = typeof message === 'string' ? message : message(item, index)
      throw new Error(errorMessage)
    }
    index++
  }
}

export function assertTuple<N extends number, Element>(
  candidate: Element[],
  length: N,
): asserts candidate is Tuple<Element, N> {
  assert(candidate.length === length, `Not a tuple of length ${length}`)
}

export function assertArrayOfTuples<N extends number, Element>(
  array: Element[][],
  length: N,
): asserts array is Tuple<Element, N>[] {
  assert(
    array.every(maybeTuple => maybeTuple.length === length),
    `Not an array of tuples length ${length}`,
  )
}
